import { lazy, Suspense } from "react";
import type { RouteObject } from "react-router-dom";

import { MainLayout } from "@/app/layout";
import {
  AuthGuard,
  GuestGuard,
  RoutePermissionGuard,
  TransferListRouteGuard,
  PreparedItemRequestAudienceGuard,
  type RoutePermissionKey,
} from "./guards";

// Loading component for lazy-loaded routes
function LoadingFallback() {
  return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" />
    </div>
  );
}

// Lazy load page components
function withSuspense(Component: React.ComponentType) {
  return function SuspenseWrapper() {
    return (
      <Suspense fallback={<LoadingFallback />}>
        <Component />
      </Suspense>
    );
  };
}

function guardedPage(
  routeKey: RoutePermissionKey,
  Component: React.ComponentType,
) {
  const Page = withSuspense(Component);
  return (
    <RoutePermissionGuard routeKey={routeKey}>
      <Page />
    </RoutePermissionGuard>
  );
}

function guardedTransferListPage(
  routeKey: RoutePermissionKey,
  listRoute: "central" | "branch",
  Component: React.ComponentType,
) {
  const Page = withSuspense(Component);
  return (
    <RoutePermissionGuard routeKey={routeKey}>
      <TransferListRouteGuard listRoute={listRoute}>
        <Page />
      </TransferListRouteGuard>
    </RoutePermissionGuard>
  );
}

function authGuardedPage(
  routeKey: RoutePermissionKey,
  Component: React.ComponentType,
) {
  const Page = withSuspense(Component);
  return (
    <AuthGuard>
      <RoutePermissionGuard routeKey={routeKey}>
        <Page />
      </RoutePermissionGuard>
    </AuthGuard>
  );
}

function guardedPreparedItemRequestPage(
  routeKey: RoutePermissionKey,
  audience: "kitchen" | "branch",
  Component: React.ComponentType,
  withAuthGuard = false,
) {
  const Page = withSuspense(Component);
  const guarded = (
    <RoutePermissionGuard routeKey={routeKey}>
      <PreparedItemRequestAudienceGuard audience={audience}>
        <Page />
      </PreparedItemRequestAudienceGuard>
    </RoutePermissionGuard>
  );

  return withAuthGuard ? <AuthGuard>{guarded}</AuthGuard> : guarded;
}

const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/features/auth/pages/RegisterPage"));
const DashboardPage = lazy(
  () => import("@/features/dashboard/pages/DashboardPage"),
);
const RawMaterialsPage = lazy(
  () => import("@/features/items/pages/RawMaterialsPage"),
);
const PreparedItemsPage = lazy(() => import("@/features/prepared-items/pages/PreparedItemsPage"));
const PreparedItemDetailsPage = lazy(() => import("@/features/prepared-items/pages/PreparedItemDetailsPage"));
const FinishSetupPage = lazy(() => import("@/features/prepared-items/pages/FinishSetupPage"));
const CreatePreparedItemPage = lazy(() => import("@/features/prepared-items/pages/CreatePreparedItemPage"));
const EditPreparedItemPage = lazy(() => import("@/features/prepared-items/pages/EditPreparedItemPage"));
const SuppliersPage = lazy(
  () => import("@/features/supplier/pages/SuppliersPage"),
);
const InventoryPage = lazy(
  () => import("@/features/Inventory/pages/InventoryPage"),
);
const SetOpenningBalancePage = lazy(
  () => import("@/features/Inventory/pages/SetOpenningBalancePage"),
);
const ItemStockDetailsPage = lazy(
  () => import("@/features/Inventory/pages/ItemStockDetailsPage"),
);
const SettingsPage = lazy(() => import("@/features/users/pages/SettingsPage"));
const ProfilePage = lazy(() => import("@/features/users/pages/ProfilePage"));
const UnauthorizedPage = lazy(
  () => import("@/shared/ui/pages/UnauthorizedPage"),
);
const NotFoundPage = lazy(() => import("@/shared/ui/pages/NotFoundPage"));
const ComponentsTestPage = lazy(
  () => import("@/shared/ui/pages/ComponentsTestPage"),
);
const CreateItemPage = lazy(
  () => import("@/features/items/pages/CreateItemPage"),
);
const ItemDetailsPage = lazy(
  () => import("@/features/items/pages/ItemDetailsPage"),
);
const CreateSupplierPage = lazy(
  () => import("@/features/supplier/pages/CreateSupplierPage"),
);
const SupplierDetailsPage = lazy(
  () => import("@/features/supplier/pages/SupplierDetailsPage"),
);
const PurchaseOrdersPage = lazy(
  () => import("@/features/purchase-orders/pages/PurchaseOrdersPage"),
);
const PurchaseOrderDetailsPage = lazy(
  () => import("@/features/purchase-orders/pages/PurchaseOrderDetailsPage"),
);
const CreatePurchaseOrderPage = lazy(
  () => import("@/features/purchase-orders/pages/CreatePurchaseOrderPage"),
);
const ReceivingQCPage = lazy(
  () => import("@/features/receiving/pages/ReceivingQCPage"),
);
const CreateReceivingOrderPage = lazy(
  () => import("@/features/receiving/pages/CreateReceivingOrderPage"),
);
const TransfersPage = lazy(
  () => import("@/features/transfers/pages/TransfersPage"),
);
const TransferDetailsPage = lazy(
  () => import("@/features/transfers/pages/TransferDetailsPage"),
);
const IncomingTransfersPage = lazy(
  () => import("@/features/transfers/pages/IncomingTransfersPage"),
);
const PreparedItemRequestsPage = lazy(
  () => import("@/features/transfers/prepared-item-requests/pages/PreparedItemRequestsPage"),
);
const CkRequestDetailsPage = lazy(
  () =>
    import(
      "@/features/transfers/prepared-item-requests/pages/CkRequestDetailsPage"
    ),
);
const AggregatedItemDetailsPage = lazy(
  () =>
    import(
      "@/features/transfers/prepared-item-requests/pages/AggregatedItemDetailsPage"
    ),
);
const ReviewPreparationPlanPage = lazy(
  () =>
    import(
      "@/features/transfers/prepared-item-requests/pages/ReviewPreparationPlanPage"
    ),
);
const SendPreparedItemsPage = lazy(
  () =>
    import(
      "@/features/transfers/prepared-item-requests/pages/SendPreparedItemsPage"
    ),
);
const RequestsSentPage = lazy(
  () => import("@/features/transfers/pages/RequestsSentPage"),
);
const RequestSentDetailsPage = lazy(
  () => import("@/features/transfers/pages/RequestSentDetailsPage"),
);
const CreatePreparedRequestPage = lazy(
  () =>
    import(
      "@/features/transfers/create-prepared-request/CreatePreparedRequestForm"
    ),
);
const BranchToBranchTransfersPage = lazy(
  () => import("@/features/transfers/pages/BranchToBranchTransferPage"),
);
const ReceiveTransferPage = lazy(
  () => import("@/features/transfers/receive-transfer/ReceiveTransferPage"),
);
const CreateTransferBranchPage = lazy(
  () =>
    import(
      "@/features/transfers/create-transfer-branch-to-branch/CreateTransferBranch"
    ),
);
const ProducedOutputPage = lazy(
  () => import("@/features/produced-output/pages/ProducedOutputPage"),
);
const ProduceOutputFormPage = lazy(
  () => import("@/features/produced-output/pages/ProduceOutputFormPage"),
);

const BatchDetailsPage = lazy(
  () => import("@/features/produced-output/pages/BatchDetails"),
);
export const routes: RouteObject[] = [
  // Public routes (Guest only)
  {
    path: "/login",
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage />
        </Suspense>
      </GuestGuard>
    ),
  },
  {
    path: "/register",
    element: (
      <GuestGuard>
        <Suspense fallback={<LoadingFallback />}>
          <RegisterPage />
        </Suspense>
      </GuestGuard>
    ),
  },

  {
    path: "/items/create-item",
    element: authGuardedPage("/items/create-item", CreateItemPage),
  },
  {
    path: "/items/:id/edit",
    element: authGuardedPage("/items/:id/edit", CreateItemPage),
  },
  {
    path: "/suppliers/create-supplier",
    element: authGuardedPage(
      "/suppliers/create-supplier",
      CreateSupplierPage,
    ),
  },
  {
    path: "/suppliers/:id/edit",
    element: authGuardedPage("/suppliers/:id/edit", CreateSupplierPage),
  },
  {
    path: "/purchase-orders/create-order",
    element: authGuardedPage(
      "/purchase-orders/create-order",
      CreatePurchaseOrderPage,
    ),
  },
  {
    path: "/receiving-orders/create/:poId",
    element: authGuardedPage(
      "/receiving-orders/create/:poId",
      CreateReceivingOrderPage,
    ),
  },
  {
    path: "/inventory/set-opening-balance",
    element: authGuardedPage(
      "/inventory/set-opening-balance",
      SetOpenningBalancePage,
    ),
  },
  {
    path: "/branch-transfers/receive-transfer/:id",
    element: authGuardedPage(
      "/branch-transfers/receive-transfer/:id",
      ReceiveTransferPage,
    ),
  },
  {
    path: "/transfers/create-transfer",
    element: authGuardedPage(
      "/transfers/create-transfer",
      CreateTransferBranchPage,
    ),
  },
  {
    path: "/prepared-item-requests/review-preparation-plan/:id",
    element: guardedPreparedItemRequestPage(
      "/prepared-item-requests/review-preparation-plan/:id",
      "kitchen",
      ReviewPreparationPlanPage,
      true,
    ),
  },
  {
    path: "/prepared-item-requests/send-items/:id",
    element: guardedPreparedItemRequestPage(
      "/prepared-item-requests/send-items/:id",
      "kitchen",
      SendPreparedItemsPage,
      true,
    ),
  },
  {
    path: "/requests-sent/create",
    element: guardedPreparedItemRequestPage(
      "/requests-sent/create",
      "branch",
      CreatePreparedRequestPage,
      true,
    ),
  },
  {
    path: "/requests-sent/:id/edit",
    element: guardedPreparedItemRequestPage(
      "/requests-sent/:id/edit",
      "branch",
      CreatePreparedRequestPage,
      true,
    ),
  },
  {
    path: "/items/prepared-items/create",
    element: authGuardedPage("/items/prepared-items/create", CreatePreparedItemPage),
  },
  {
    path: "/items/prepared-items/:id/edit",
    element: authGuardedPage("/items/prepared-items/:id/edit", EditPreparedItemPage),
  },

  {
    path: "/prepared-items/finish-items-setup",
    element: authGuardedPage(
      "/prepared-items/finish-items-setup",
      FinishSetupPage,
    ),
  },
  {
    path: "/preparation-management/produce",
    element: authGuardedPage(
      "/preparation-management/produce",
      ProduceOutputFormPage,
    ),
  },
  {
    path: "/",
    element: (
      <AuthGuard>
        <MainLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: withSuspense(DashboardPage)(),
      },
      {
        path: "dashboard",
        element: withSuspense(DashboardPage)(),
      },

      {
        path: "inventory",
        children: [
          {
            index: true,
            element: guardedPage("/inventory", InventoryPage),
          },
          {
            path: ":id",
            element: guardedPage("/inventory/:id", ItemStockDetailsPage),
          },
        ],
      },

      {
        path: "receiving-qc",
        element: guardedPage("/receiving-qc", ReceivingQCPage),
      },

      {
        path: "transfers",
        element: guardedTransferListPage("/transfers", "central", TransfersPage),
      },
      {
        path: "transfers/:id",
        element: guardedPage("/transfers/:id", TransferDetailsPage),
      },
      {
        path: "incoming-transfers",
        element: guardedPage("/incoming-transfers", IncomingTransfersPage),
      },
      {
        path: "prepared-item-requests",
        element: guardedPreparedItemRequestPage(
          "/prepared-item-requests",
          "kitchen",
          PreparedItemRequestsPage,
        ),
      },
      {
        path: "prepared-item-requests/aggregated-item-details/:id",
        element: guardedPreparedItemRequestPage(
          "/prepared-item-requests/aggregated-item-details/:id",
          "kitchen",
          AggregatedItemDetailsPage,
        ),
      },
      {
        path: "prepared-item-requests/:id",
        element: guardedPreparedItemRequestPage(
          "/prepared-item-requests/:id",
          "kitchen",
          CkRequestDetailsPage,
        ),
      },
      {
        path: "requests-sent",
        element: guardedPreparedItemRequestPage(
          "/requests-sent",
          "branch",
          RequestsSentPage,
        ),
      },
      {
        path: "requests-sent/:id",
        element: guardedPreparedItemRequestPage(
          "/requests-sent/:id",
          "branch",
          RequestSentDetailsPage,
        ),
      },
      {
        path: "branch-transfers",
        element: guardedTransferListPage(
          "/branch-transfers",
          "branch",
          BranchToBranchTransfersPage,
        ),
      },

      {
        path: "purchase-orders",
        children: [
          {
            index: true,
            element: guardedPage("/purchase-orders", PurchaseOrdersPage),
          },
          {
            path: ":id",
            element: guardedPage(
              "/purchase-orders/:id",
              PurchaseOrderDetailsPage,
            ),
          },
        ],
      },

      {
        path: "items",
        children: [
          {
            index: true,
            element: guardedPage("/items", RawMaterialsPage),
          },
          {
            path: "raw-materials",
            element: guardedPage("/items/raw-materials", RawMaterialsPage),
          },
          {
            path: "prepared-items",
            element: guardedPage("/items/prepared-items", PreparedItemsPage),
          },
          {
            path: "prepared-items/:id",
            element: guardedPage(
              "/items/prepared-items/:id",
              PreparedItemDetailsPage,
            ),
          },
          {
            path: ":id",
            element: guardedPage("/items/:id", ItemDetailsPage),
          },
        ],
      },

      {
        path: "suppliers",
        children: [
          {
            index: true,
            element: guardedPage("/suppliers", SuppliersPage),
          },
          {
            path: ":id",
            element: guardedPage("/suppliers/:id", SupplierDetailsPage),
          },
        ],
      },

      {
        path: "preparation-management",
        children: [
          {
            index: true,
            element: guardedPage(
              "/preparation-management",
              ProducedOutputPage,
            ),
          },
          {
            path: "batch-details/:id",
            element: guardedPage(
              "/preparation-management/batch-details/:id",
              BatchDetailsPage,
            ),
          },
        ],
      },

      {
        path: "profile",
        element: withSuspense(ProfilePage)(),
      },

      {
        path: "settings",
        element: withSuspense(SettingsPage)(),
      },

      {
        path: "components-test",
        element: withSuspense(ComponentsTestPage)(),
      },
    ],
  },

  // Unauthorized page
  {
    path: "/unauthorized",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <UnauthorizedPage />
      </Suspense>
    ),
  },

  // 404 Not Found
  {
    path: "*",
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
];
